/**
 * Reads `exp` out of an access token so the client can refresh proactively,
 * before a request fails with 401.
 *
 * No signature verification happens here and none is needed — the value is
 * only used to decide *when* to refresh. The API verifies the signature on
 * every request; a tampered token simply gets rejected there.
 */

/** Milliseconds since the epoch at which the token expires, or null. */
export function getJwtExpiresAtMs(token: string): number | null {
  // Runs during `gatsby build` (Node) as well as in the browser, and atob is
  // not defined in older Node globals.
  if (typeof globalThis.atob !== 'function') return null;

  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(globalThis.atob(base64)) as { exp?: number };
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * True when the token expires within `leewayMs` (or already has). An
 * undecodable token is treated as fresh: the request will fail with 401 and
 * the response interceptor handles it, which is safer than refresh-looping.
 */
export function isAccessTokenStale(token: string, leewayMs: number): boolean {
  const expiresAt = getJwtExpiresAtMs(token);
  if (expiresAt === null) return false;
  return expiresAt - Date.now() <= leewayMs;
}
